<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import crmApi from 'dashboard/api/crm';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [{}],
  },
  actionName: {
    type: String,
    default: '',
  },
  funnels: {
    type: Array,
    default: () => [],
  },
  crmOrigins: {
    type: Array,
    default: () => [],
  },
  agents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const stages = ref([]);
const loadingStages = ref(false);

const params = computed(() => {
  const v = props.modelValue;
  if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object')
    return { ...v[0] };
  return {};
});

const isCreate = computed(() => props.actionName === 'create_crm_opportunity');
const isUpdate = computed(() => props.actionName === 'update_crm_opportunity');

const statusOptions = computed(() => [
  { id: 'open', name: t('AUTOMATION.CRM.STATUS_OPEN') },
  { id: 'won', name: t('AUTOMATION.CRM.STATUS_WON') },
  { id: 'lost', name: t('AUTOMATION.CRM.STATUS_LOST') },
]);

const update = patch => {
  emit('update:modelValue', [{ ...params.value, ...patch }]);
};

const onFunnelChange = async funnelId => {
  update({ funnel_id: funnelId, funnel_stage_id: undefined });
  stages.value = [];
  if (!funnelId) return;
  loadingStages.value = true;
  try {
    const { data } = await crmApi.getStages(funnelId);
    stages.value = data.payload || data;
  } finally {
    loadingStages.value = false;
  }
};

const onStageChange = stageId => update({ funnel_stage_id: stageId });
const onStatusChange = value => update({ status: value || undefined });
const onTitleChange = value => update({ title: value || undefined });
const onValueChange = value => update({ value: value || undefined });
const onOriginChange = originId =>
  update({ crm_origin_id: originId || undefined });
const onAgentChange = agentId =>
  update({ assigned_agent_id: agentId || undefined });
const onProbabilityChange = value =>
  update({ probability: value || undefined });
const onCloseDateChange = value => update({ close_date: value || undefined });
const onProductServiceChange = value =>
  update({ product_service: value || undefined });
const onTagsChange = value => update({ tags: value || undefined });
const onNotesChange = value => update({ notes: value || undefined });

watch(
  () => params.value.funnel_id,
  async funnelId => {
    if (!funnelId || stages.value.length > 0) return;
    loadingStages.value = true;
    try {
      const { data } = await crmApi.getStages(funnelId);
      stages.value = data.payload || data;
    } finally {
      loadingStages.value = false;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-2 flex-1">
    <template v-if="isUpdate">
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.status || ''"
        @change="onStatusChange($event.target.value || undefined)"
      >
        <option value="">{{ t('AUTOMATION.CRM.STATUS_PLACEHOLDER') }}</option>
        <option v-for="s in statusOptions" :key="s.id" :value="s.id">
          {{ s.name }}
        </option>
      </select>
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.funnel_id || ''"
        @change="
          onFunnelChange(
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">
          {{ t('AUTOMATION.CRM.FUNNEL_PLACEHOLDER_OPTIONAL') }}
        </option>
        <option v-for="f in funnels" :key="f.id" :value="f.id">
          {{ f.name }}
        </option>
      </select>
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500 disabled:opacity-50"
        :value="params.funnel_stage_id || ''"
        :disabled="!params.funnel_id || loadingStages"
        @change="
          onStageChange(
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">
          {{ t('AUTOMATION.CRM.STAGE_PLACEHOLDER_OPTIONAL') }}
        </option>
        <option v-for="s in stages" :key="s.id" :value="s.id">
          {{ s.name }}
        </option>
      </select>
    </template>
    <template v-else-if="isCreate">
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.funnel_id || ''"
        @change="
          onFunnelChange(
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">{{ t('AUTOMATION.CRM.FUNNEL_PLACEHOLDER') }}</option>
        <option v-for="f in funnels" :key="f.id" :value="f.id">
          {{ f.name }}
        </option>
      </select>
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500 disabled:opacity-50"
        :value="params.funnel_stage_id || ''"
        :disabled="!params.funnel_id || loadingStages"
        @change="
          onStageChange(
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">{{ t('AUTOMATION.CRM.STAGE_PLACEHOLDER') }}</option>
        <option v-for="s in stages" :key="s.id" :value="s.id">
          {{ s.name }}
        </option>
      </select>
    </template>

    <template v-if="isCreate || isUpdate">
      <input
        type="text"
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.title || ''"
        :placeholder="t('AUTOMATION.CRM.TITLE_PLACEHOLDER')"
        @input="onTitleChange($event.target.value)"
      />
      <input
        type="number"
        min="0"
        step="0.01"
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.value || ''"
        :placeholder="t('AUTOMATION.CRM.VALUE_PLACEHOLDER')"
        @input="onValueChange($event.target.value)"
      />
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.crm_origin_id || ''"
        @change="
          onOriginChange(
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">{{ t('AUTOMATION.CRM.ORIGIN_PLACEHOLDER') }}</option>
        <option v-for="o in crmOrigins" :key="o.id" :value="o.id">
          {{ o.name }}
        </option>
      </select>
      <select
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.assigned_agent_id || ''"
        @change="
          onAgentChange(
            $event.target.value ? Number($event.target.value) : null
          )
        "
      >
        <option value="">{{ t('AUTOMATION.CRM.AGENT_PLACEHOLDER') }}</option>
        <option v-for="a in agents" :key="a.id" :value="a.id">
          {{ a.name }}
        </option>
      </select>
      <input
        type="number"
        min="0"
        max="100"
        step="1"
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.probability || ''"
        :placeholder="t('AUTOMATION.CRM.PROBABILITY_PLACEHOLDER')"
        @input="onProbabilityChange($event.target.value)"
      />
      <input
        type="date"
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.close_date || ''"
        @change="onCloseDateChange($event.target.value)"
      />
      <input
        type="text"
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.product_service || ''"
        :placeholder="t('AUTOMATION.CRM.PRODUCT_SERVICE_PLACEHOLDER')"
        @input="onProductServiceChange($event.target.value)"
      />
      <input
        type="text"
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500"
        :value="params.tags || ''"
        :placeholder="t('AUTOMATION.CRM.TAGS_PLACEHOLDER')"
        @input="onTagsChange($event.target.value)"
      />
      <textarea
        class="w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-woot-500 resize-none"
        rows="3"
        :value="params.notes || ''"
        :placeholder="t('AUTOMATION.CRM.NOTES_PLACEHOLDER')"
        @input="onNotesChange($event.target.value)"
      />
    </template>
  </div>
</template>
